/**
 * Tender Chat Store
 *
 * Bid-scoped tender chat, running on the Unified Agentic Pipeline (orchestrator)
 * transport — the same engine that powers the section editor, entered through the
 * bid-scope chat lane. Conversations are agent THREADS within a single per-user
 * bid chat session. Streaming, citations, and honest retrieval status all ride
 * the agent StreamEvent union.
 *
 * This store deliberately does NOT import from agent-store: the small patterns it
 * needs (citation accumulation, positional quote merge, replay resume) are ported
 * here so the editor panel stays untouched.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import {
  getOrCreateBidChatSession,
  createAgentThread,
  activateAgentThread,
  updateAgentThread,
  deleteAgentThread,
  type AgentThread,
  type BidChatSessionResponse,
  type BidChatTurnHistoryEntry,
} from '@/lib/api'
import {
  sendAgentTurn,
  replayTurnEvents,
  cancelAgentTurn,
  type StreamEvent,
} from '@/lib/agent-client'
import {
  reduceActivityPhases,
  initialPhaseState,
  optimisticPhaseState,
  type PhaseState,
} from '@/lib/activity-phases'
import { deriveRetrievalDetail } from '@/lib/retrieval-status'
import {
  appendMemorySavedLine,
  dropOrSettleReply,
  parseMemorySavedLine,
  undoMemorySavedLine,
} from '@/lib/memory-saved'
import type { ChatMessage, SourceHighlightData } from '@/stores/assistant-store'

// ===================
// Types
// ===================

type CitationSource = NonNullable<ChatMessage['sources']>[number]
type LibraryCitationSource = NonNullable<ChatMessage['librarySources']>[number]

interface TenderChatState {
  // Session / threads
  sessionId: string | null
  activeThreadId: string | null
  threads: AgentThread[]
  /** threadId → first-user-message preview, for the history sidebar labels. */
  threadPreviews: Record<string, string>
  messages: ChatMessage[]

  // Loading / streaming
  isLoading: boolean
  isStreaming: boolean
  streamingContent: string
  error: string | null

  // The bid this store is scoped to
  bidId: string | null

  // In-flight turn (for cancel + replay resume)
  currentTurnId: string | null

  /**
   * Raw last stream event — a clean seam for the activity-phase reducer, which
   * subscribes to this field (store-level subscription, below). Kept typed and
   * side-effect-free here.
   */
  lastStreamEvent: StreamEvent | null

  /**
   * §2.7 activity phases for the in-flight/last turn. Derived by the store-level
   * subscription that folds `lastStreamEvent` through `reduceActivityPhases`.
   * Read whole by `<ActivityPhases phases={...} />`.
   */
  activityPhases: PhaseState

  // Actions
  setBidId: (bidId: string) => void
  reloadActiveThread: () => Promise<void>
  selectThread: (threadId: string) => Promise<void>
  startNewChat: () => Promise<void>
  /** Rethrows on failure so the rename dialog can stay open. */
  renameThread: (threadId: string, name: string) => Promise<void>
  togglePinThread: (threadId: string) => Promise<void>
  /** Rethrows on failure so callers can observe it; `error` carries the message. */
  deleteThread: (threadId: string) => Promise<void>
  /** Resolves `true` once the turn was dispatched; `false` when the session
   * wasn't ready, so the composer can keep the user's draft. */
  sendMessage: (
    content: string,
    attachments?: Array<{ attachmentId: string; s3Key: string; filename: string; mimeType: string; size: number }>
  ) => Promise<boolean>
  cancelStream: () => void
  clearError: () => void
  /**
   * Undo a "Saved to memory" line: deletes the item. Never throws; the line's
   * status carries the outcome ("Already removed" when someone got there first).
   */
  undoMemorySaved: (messageId: string, memoryId: string) => Promise<void>
}

// ===================
// Module-level abort controller for stream cancellation
// ===================

let streamAbortController: AbortController | null = null

// ===================
// Store Implementation
// ===================

export const useTenderChatStore = create<TenderChatState>()(
  devtools(
    (set, get) => ({
      // Initial state
      sessionId: null,
      activeThreadId: null,
      threads: [],
      threadPreviews: {},
      messages: [],
      isLoading: false,
      isStreaming: false,
      streamingContent: '',
      error: null,
      bidId: null,
      currentTurnId: null,
      lastStreamEvent: null,
      activityPhases: initialPhaseState(),

      // ===================
      // Bid Scoping / Session lifecycle
      // ===================

      setBidId: (bidId: string) => {
        if (get().bidId === bidId) return

        // Abort the previous bid's in-flight stream BEFORE resetting — its
        // event handlers write into this shared store, so an un-aborted stream
        // would bleed the old bid's chunks/activity into the new bid's chat and
        // its terminal event would clobber the new bid's streaming state.
        streamAbortController?.abort()
        streamAbortController = null

        // Reset all state for the new bid, then get-or-create its chat session.
        set({
          bidId,
          sessionId: null,
          activeThreadId: null,
          threads: [],
          threadPreviews: {},
          messages: [],
          isLoading: false,
          isStreaming: false,
          streamingContent: '',
          error: null,
          currentTurnId: null,
          lastStreamEvent: null,
          activityPhases: initialPhaseState(),
        })

        void get().reloadActiveThread()
      },

      reloadActiveThread: async () => {
        const { bidId } = get()
        if (!bidId) return

        try {
          set({ isLoading: true, error: null })
          const response = await getOrCreateBidChatSession(bidId)
          // Bid-identity gate (same pattern as tender-overview-store): rapid
          // A→B→A navigation can resolve these requests out of order, and a
          // stale response would render another bid's session/threads/messages.
          if (get().bidId !== bidId) return
          applySessionResponse(set, response)
        } catch (error) {
          if (get().bidId !== bidId) return
          set({
            error: error instanceof Error ? error.message : 'Failed to load tender chat',
            isLoading: false,
          })
        }
      },

      selectThread: async (threadId: string) => {
        const { sessionId, activeThreadId, bidId } = get()
        if (!sessionId || !bidId || threadId === activeThreadId) return

        // Abort any in-flight stream before switching threads.
        streamAbortController?.abort()
        streamAbortController = null

        // Clear any streaming placeholders before switching threads.
        set((state) => ({
          isStreaming: false,
          streamingContent: '',
          activityPhases: initialPhaseState(),
          messages: state.messages.filter((m) => !m.isStreaming),
        }))

        try {
          set({ isLoading: true, error: null })
          await activateAgentThread(sessionId, threadId)
          // Re-fetch the session: the backend returns the now-active thread's
          // history (activate itself returns no turns).
          const response = await getOrCreateBidChatSession(bidId)
          applySessionResponse(set, response)
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to open conversation',
            isLoading: false,
          })
        }
      },

      startNewChat: async () => {
        const { sessionId } = get()

        // Abort any in-flight stream before clearing streaming state.
        streamAbortController?.abort()
        streamAbortController = null

        if (!sessionId) {
          set({ messages: [], streamingContent: '', isStreaming: false, error: null })
          return
        }

        set((state) => ({
          isLoading: true,
          error: null,
          isStreaming: false,
          streamingContent: '',
          messages: state.messages.filter((m) => !m.isStreaming).length === 0 ? state.messages : [],
        }))

        try {
          const response = await createAgentThread(sessionId, 'fresh')
          const newThreadId = response.activeThreadId ?? response.thread.id
          set((state) => ({
            activeThreadId: newThreadId,
            threads: [response.thread, ...state.threads.filter((t) => t.id !== response.thread.id)],
            messages: [],
            isLoading: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to start a new chat',
            isLoading: false,
          })
        }
      },

      // ===================
      // Thread management (rename / pin / delete)
      // ===================

      renameThread: async (threadId: string, name: string) => {
        const { sessionId, threads } = get()
        if (!sessionId) return

        const previousThreads = threads
        set({ threads: threads.map((t) => (t.id === threadId ? { ...t, name } : t)) })

        try {
          await updateAgentThread(sessionId, threadId, { name })
        } catch (error) {
          set({
            threads: previousThreads,
            error: error instanceof Error ? error.message : 'Failed to rename conversation',
          })
          throw error
        }
      },

      togglePinThread: async (threadId: string) => {
        const { sessionId, threads } = get()
        if (!sessionId) return

        const thread = threads.find((t) => t.id === threadId)
        if (!thread) return

        const previousThreads = threads
        const isPinned = !thread.isPinned
        set({ threads: threads.map((t) => (t.id === threadId ? { ...t, isPinned } : t)) })

        try {
          await updateAgentThread(sessionId, threadId, { isPinned })
        } catch (error) {
          set({
            threads: previousThreads,
            error: error instanceof Error ? error.message : 'Failed to update conversation',
          })
        }
      },

      deleteThread: async (threadId: string) => {
        const { sessionId, activeThreadId, bidId } = get()
        if (!sessionId) return

        const wasActive = threadId === activeThreadId

        if (wasActive) {
          // Same guard as selectThread: never leave a stream writing into a
          // thread that no longer exists.
          streamAbortController?.abort()
          streamAbortController = null
        }

        try {
          // The backend owns the next active thread (most recent remaining, or a
          // freshly seeded one) — adopt what it returns, never compute it here.
          const response = await deleteAgentThread(sessionId, threadId)
          set({
            threads: response.threads,
            activeThreadId: response.activeThreadId,
            ...(wasActive
              ? { messages: [], isStreaming: false, streamingContent: '', activityPhases: initialPhaseState() }
              : {}),
          })

          if (wasActive && bidId) {
            // The replacement thread may carry history — load it the same way
            // selectThread does.
            const sessionResponse = await getOrCreateBidChatSession(bidId)
            applySessionResponse(set, sessionResponse)
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete conversation',
          })
          throw error
        }
      },

      // ===================
      // Send / streaming
      // ===================

      sendMessage: async (content, attachments) => {
        const { bidId } = get()
        if (!bidId) return false

        // Ensure a session + active thread exist (defensive — setBidId primes them).
        let { sessionId, activeThreadId } = get()
        if (!sessionId || !activeThreadId) {
          await get().reloadActiveThread()
          sessionId = get().sessionId
          activeThreadId = get().activeThreadId
          if (!sessionId || !activeThreadId) {
            set({ error: 'Tender chat is not ready yet. Please try again.' })
            return false
          }
        }

        const scope = { kind: 'bid' as const, bidId }

        // Optimistic user message
        const userMessage: ChatMessage = {
          id: crypto.randomUUID(),
          sessionId,
          role: 'user',
          content,
          createdAt: new Date().toISOString(),
          ...(attachments && attachments.length > 0 && {
            attachments: attachments.map((a) => ({
              attachmentId: a.attachmentId,
              filename: a.filename,
              mimeType: a.mimeType,
              size: a.size,
            })),
          }),
        }

        const streamingId = `streaming-${crypto.randomUUID()}`
        const streamingPlaceholder: ChatMessage = {
          id: streamingId,
          sessionId,
          role: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
          isStreaming: true,
        }

        // Record a preview for the active thread if it doesn't have one yet.
        set((state) => ({
          messages: [...state.messages, userMessage, streamingPlaceholder],
          isStreaming: true,
          streamingContent: '',
          error: null,
          currentTurnId: null,
          activityPhases: optimisticPhaseState(),
          threadPreviews: activeThreadId && !state.threadPreviews[activeThreadId]
            ? { ...state.threadPreviews, [activeThreadId]: previewFromContent(content) }
            : state.threadPreviews,
        }))

        // Per-turn accumulators (ported from the panel; local, not shared).
        const turnCitations: CitationSource[] = []
        const turnLibraryCitations: LibraryCitationSource[] = []
        let retrievalStatus: 'ok' | 'partial' | 'empty' | 'weak' | undefined
        let fallbackUsed = false
        let lastSeq = 0
        let sawTerminal = false

        // User-initiated retries rotate the key; transport retries (replay) reuse it.
        const idempotencyKey = crypto.randomUUID()

        const handleEvent = (event: StreamEvent) => {
          // Late events from an aborted stream (bid/thread switch raced the
          // transport) must not touch the store — their state belongs to a
          // context that no longer exists. (myController is assigned before
          // sendAgentTurn ever delivers an event.)
          if (myController.signal.aborted) return
          // Seq de-dup so replay after a drop never double-applies chunks.
          if (event.seq > 0) {
            if (event.seq <= lastSeq) return
            lastSeq = event.seq
          }
          if (event.turnId && get().currentTurnId !== event.turnId) {
            set({ currentTurnId: event.turnId })
          }
          // Task 5 seam: expose the raw event to subscribers.
          set({ lastStreamEvent: event })

          switch (event.eventType) {
            case 'assistant_message_chunk': {
              const chunk =
                (event.data['text'] as string | undefined) ??
                (event.data['chunk'] as string | undefined) ??
                ''
              if (!chunk) break
              set((state) => ({
                messages: state.messages.map((m) =>
                  m.id === streamingId ? { ...m, content: m.content + chunk, isStreaming: true } : m
                ),
                streamingContent: state.streamingContent + chunk,
              }))
              break
            }

            case 'evidence_search_completed': {
              mergeCitations(turnCitations, event.data['citations'])
              mergeCitations(turnLibraryCitations, event.data['libraryCitations'])
              const status = event.data['retrievalStatus']
              if (status === 'ok' || status === 'partial' || status === 'empty' || status === 'weak') {
                retrievalStatus = status
              }
              if (event.data['fallbackUsed'] === true) fallbackUsed = true
              break
            }

            case 'citation_quotes': {
              mergeQuotes(turnCitations, turnLibraryCitations, event.data['quotes'])
              break
            }

            case 'memory_saved': {
              // The agent saved something to bid memory: record it on this
              // turn's reply so the bubble shows "Saved to memory · Undo".
              const line = parseMemorySavedLine(event.data)
              if (!line) break
              set((state) => ({ messages: appendMemorySavedLine(state.messages, streamingId, line) }))
              break
            }

            case 'turn_completed': {
              sawTerminal = true
              finalizeAssistantMessage(set, streamingId, {
                citations: turnCitations,
                libraryCitations: turnLibraryCitations,
                retrievalStatus,
                fallbackUsed,
              })
              break
            }

            case 'turn_error': {
              sawTerminal = true
              const rawMessage = event.data['message']
              const message = typeof rawMessage === 'string' && rawMessage.length > 0
                ? rawMessage
                : 'The assistant ran into a problem. Please try again.'
              set((state) => ({
                messages: dropOrSettleReply(state.messages, (m) => m.id === streamingId),
                isStreaming: false,
                streamingContent: '',
                currentTurnId: null,
                error: message,
              }))
              break
            }

            // heartbeat and all editor-only events: no-op for chat.
            default:
              break
          }
        }

        const myController = new AbortController()
        streamAbortController = myController
        try {
          await sendAgentTurn(
            {
              sessionId,
              threadId: activeThreadId,
              scope,
              message: content,
              idempotencyKey,
              ...(attachments && attachments.length > 0 ? { attachments } : {}),
            },
            handleEvent,
            myController.signal
          )

          // Stream ended cleanly but no terminal event arrived — finalize what we have.
          if (!sawTerminal && get().isStreaming) {
            finalizeAssistantMessage(set, streamingId, {
              citations: turnCitations,
              libraryCitations: turnLibraryCitations,
              retrievalStatus,
              fallbackUsed,
            })
          }
        } catch (error) {
          const code = errorCode(error)

          if (code === 'CANCELLED') {
            // cancelStream already tidied state.
            return true
          }

          // Transport dropped mid-stream — try to resume from where we left off.
          // Pass the same controller's signal so Stop during reconnect aborts
          // the replay fetch too.
          if (
            (code === 'STREAM_ABORTED' || code === 'STREAM_IDLE_TIMEOUT' || code === 'TIMEOUT') &&
            get().currentTurnId
          ) {
            const turnId = get().currentTurnId as string
            try {
              await replayTurnEvents(turnId, lastSeq, handleEvent, myController.signal)
              if (!sawTerminal && get().isStreaming) {
                finalizeAssistantMessage(set, streamingId, {
                  citations: turnCitations,
                  libraryCitations: turnLibraryCitations,
                  retrievalStatus,
                  fallbackUsed,
                })
              }
              return true
            } catch {
              // fall through to error handling below
            }
          }

          set((state) => ({
            messages: dropOrSettleReply(state.messages, (m) => m.id === streamingId),
            isStreaming: false,
            streamingContent: '',
            currentTurnId: null,
            error: error instanceof Error ? error.message : 'Failed to send message',
          }))
        } finally {
          // Identity guard: only clear the module controller if it is still OURS.
          // A newer send may have replaced it; nulling then would strand that
          // send's cancel/replay wiring.
          if (streamAbortController === myController) streamAbortController = null
        }

        return true
      },

      cancelStream: () => {
        streamAbortController?.abort()
        streamAbortController = null
        const { currentTurnId, sessionId } = get()
        if (currentTurnId || sessionId) {
          void cancelAgentTurn({
            ...(currentTurnId ? { turnId: currentTurnId } : {}),
            ...(sessionId ? { sessionId } : {}),
          }).catch(() => { /* best-effort */ })
        }
        set((state) => ({
          isStreaming: false,
          streamingContent: '',
          currentTurnId: null,
          messages: dropOrSettleReply(state.messages, (m) => m.isStreaming === true),
        }))
      },

      clearError: () => {
        set({ error: null })
      },

      undoMemorySaved: (messageId, memoryId) =>
        undoMemorySavedLine(
          () => get().messages,
          (update) => set((state) => ({ messages: update(state.messages) })),
          messageId,
          memoryId
        ),
    }),
    { name: 'tender-chat-store' }
  )
)

// ===================
// §2.7 activity-phase feed (store-level subscription)
// ===================

/**
 * Fold every raw `lastStreamEvent` through the activity-phase reducer.
 *
 * This MUST be a store-level subscription (not a React render selector): the
 * subscription callback fires synchronously on every `set`, so it observes every
 * event in order — React batching would skip intermediate events. The identity
 * guard on `lastStreamEvent` makes the re-entrant `setState` below terminate
 * (updating `activityPhases` doesn't change `lastStreamEvent`).
 */
useTenderChatStore.subscribe((state, prev) => {
  if (state.lastStreamEvent === prev.lastStreamEvent) return
  const event = state.lastStreamEvent
  if (!event) return
  const next = reduceActivityPhases(state.activityPhases, event)
  if (next !== state.activityPhases) {
    useTenderChatStore.setState({ activityPhases: next })
  }
})

// ===================
// Helper Functions
// ===================

type SetState = (
  partial:
    | Partial<TenderChatState>
    | ((state: TenderChatState) => Partial<TenderChatState>)
) => void

/** Copy a session/threads/history response into the store state. */
function applySessionResponse(set: SetState, response: BidChatSessionResponse) {
  const activeThreadId = response.activeThreadId
  const relevant = activeThreadId
    ? response.turnHistory.filter((t) => !t.threadId || t.threadId === activeThreadId)
    : response.turnHistory
  const built = buildThreadPreviews(response.turnHistory)
  // Merge, don't replace: the backend returns only the active thread's history,
  // so a wholesale replace would drop previews stamped for other threads when
  // switching away and back. Merge keeps previously-stamped previews alive.
  set((state) => ({
    sessionId: response.sessionId,
    activeThreadId,
    threads: response.threads,
    threadPreviews: { ...state.threadPreviews, ...built },
    messages: hydrateMessages(relevant, response.sessionId),
    isLoading: false,
  }))
}

/**
 * Every Tender Chat turn is bid-scoped, and a bid-scoped prompt always carries
 * the saved bid record (the RECORDED BID DEADLINE block, title, client, bid
 * memory). An empty document search therefore never means the answer came from
 * general knowledge alone.
 */
const TENDER_CHAT_RETRIEVAL_OPTIONS = { hadSavedBidData: true } as const

/** Map persisted turn history into user/assistant ChatMessages. */
function hydrateMessages(
  turnHistory: BidChatTurnHistoryEntry[],
  sessionId: string
): ChatMessage[] {
  const messages: ChatMessage[] = []
  for (const turn of turnHistory) {
    messages.push({
      id: `${turn.turnId}-user`,
      sessionId,
      role: 'user',
      content: turn.userMessage,
      createdAt: turn.createdAt,
      ...(turn.attachments && turn.attachments.length > 0
        ? { attachments: turn.attachments }
        : {}),
    })
    if (turn.assistantResponse != null) {
      const sources = mapPersistedCitations(turn.assistantMetadata?.citations)
      const librarySources = mapPersistedCitations(turn.assistantMetadata?.libraryCitations)
      // Persisted honest-status: map retrievalStatus/fallbackUsed → the same
      // retrievalStatus + retrievalDetail the live stream sets, so the honest
      // line survives a reload. Gating mirrors finalizeAssistantMessage.
      const persistedStatus = turn.assistantMetadata?.retrievalStatus
      const retrievalDetail = deriveRetrievalDetail(
        persistedStatus,
        turn.assistantMetadata?.fallbackUsed ?? false,
        TENDER_CHAT_RETRIEVAL_OPTIONS
      )
      messages.push({
        id: `${turn.turnId}-assistant`,
        sessionId,
        role: 'assistant',
        content: turn.assistantResponse,
        createdAt: turn.completedAt ?? turn.createdAt,
        ...(sources.length > 0 ? { sources } : {}),
        ...(librarySources.length > 0 ? { librarySources } : {}),
        ...(persistedStatus && persistedStatus !== 'ok' ? { retrievalStatus: persistedStatus } : {}),
        ...(retrievalDetail ? { retrievalDetail } : {}),
      })
    }
  }
  return messages
}

type PersistedCitation = NonNullable<
  NonNullable<BidChatTurnHistoryEntry['assistantMetadata']>['citations']
>[number]

function mapPersistedCitations(raw: PersistedCitation[] | undefined): CitationSource[] {
  if (!Array.isArray(raw)) return []
  return raw.map((c): CitationSource => {
    const src: CitationSource = {
      documentId: c.documentId,
      documentName: c.documentName,
      pageNumber: c.pageNumber ?? null,
    }
    if (typeof c.snippet === 'string') src.snippet = c.snippet
    if (typeof c.quote === 'string') src.quote = c.quote
    if (c.highlightData) src.highlightData = c.highlightData as SourceHighlightData
    return src
  })
}

/** First user message per thread, for the sidebar labels. */
function buildThreadPreviews(turnHistory: BidChatTurnHistoryEntry[]): Record<string, string> {
  const previews: Record<string, string> = {}
  for (const turn of turnHistory) {
    const threadId = turn.threadId
    if (!threadId || previews[threadId]) continue
    previews[threadId] = previewFromContent(turn.userMessage)
  }
  return previews
}

function previewFromContent(content: string): string {
  const cleaned = content.trim().replace(/\s+/g, ' ')
  const maxLength = 50
  if (cleaned.length <= maxLength) return cleaned
  const truncated = cleaned.slice(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  return (lastSpace > 20 ? truncated.slice(0, lastSpace) : truncated) + '…'
}

/**
 * Accumulate citations from an `evidence_search_completed` payload. Later
 * evidence passes replace earlier ones (matches the panel — the final search
 * owns the rendered citation set).
 */
function mergeCitations(target: CitationSource[], raw: unknown) {
  if (!Array.isArray(raw) || raw.length === 0) return
  target.length = 0
  for (const entry of raw) {
    const c = entry as Record<string, unknown>
    const src: CitationSource = {
      documentId: c['documentId'] as string,
      documentName: c['documentName'] as string,
      pageNumber: (c['pageNumber'] as number | null) ?? null,
    }
    if (typeof c['snippet'] === 'string') src.snippet = c['snippet']
    if (typeof c['quote'] === 'string') src.quote = c['quote']
    if (c['highlightData']) src.highlightData = c['highlightData'] as SourceHighlightData
    target.push(src)
  }
}

/**
 * Positional quote merge (ported from the editor panel — NOT imported):
 * label "N" → citations[N-1], "LN" → libraryCitations[N-1]. Unparseable or
 * out-of-range labels are ignored; no other citation field is clobbered.
 */
function mergeQuotes(
  citations: CitationSource[],
  libraryCitations: LibraryCitationSource[],
  raw: unknown
) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return
  for (const [label, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'string' || value.length === 0) continue
    const match = /^(L?)(\d+)$/.exec(label)
    if (!match) continue
    const isLibrary = match[1] === 'L'
    const idx = Number.parseInt(match[2]!, 10)
    if (!Number.isInteger(idx) || idx < 1) continue
    const citation = (isLibrary ? libraryCitations : citations)[idx - 1]
    if (citation) citation.quote = value
  }
}

/** Finalize the streaming assistant message: attach citations + honest status. */
function finalizeAssistantMessage(
  set: SetState,
  streamingId: string,
  turn: {
    citations: CitationSource[]
    libraryCitations: LibraryCitationSource[]
    retrievalStatus: 'ok' | 'partial' | 'empty' | 'weak' | undefined
    fallbackUsed: boolean
  }
) {
  const sources = turn.citations.length > 0 ? turn.citations.map((c) => ({ ...c })) : undefined
  const librarySources = turn.libraryCitations.length > 0
    ? turn.libraryCitations.map((c) => ({ ...c }))
    : undefined
  const retrievalDetail = deriveRetrievalDetail(
    turn.retrievalStatus,
    turn.fallbackUsed,
    TENDER_CHAT_RETRIEVAL_OPTIONS
  )

  set((state) => ({
    messages: state.messages.map((m) =>
      m.id === streamingId
        ? {
            ...m,
            isStreaming: false,
            ...(sources ? { sources } : {}),
            ...(librarySources ? { librarySources } : {}),
            ...(turn.retrievalStatus && turn.retrievalStatus !== 'ok'
              ? { retrievalStatus: turn.retrievalStatus }
              : {}),
            ...(retrievalDetail ? { retrievalDetail } : {}),
          }
        : m
    ),
    isStreaming: false,
    streamingContent: '',
    currentTurnId: null,
  }))
}

function errorCode(error: unknown): string | null {
  if (error instanceof Error && 'code' in error && typeof (error as { code?: unknown }).code === 'string') {
    return (error as { code: string }).code
  }
  return null
}
